import { ValidationProblemDetails } from "@utdanningsdirektoratet/utils/object";

export type Values<T> = { [K in keyof T]: T[K] };
export type Message<T> =
  | string
  | ((value: T[keyof T], values: Values<T>) => string);

export type MappedProps<T> = {
  [K in keyof T]: {
    value?: T[K];
    emptyValue?: T[K] | null | undefined;
    validation?: Array<ValidationType<T, T[K]>>;
    emptyStringAsNull?: boolean;
    dependOn?: Array<Exclude<keyof T, K>>;
  };
};

export type ValidationType<T, K = T[keyof T]> = {
  validation: (input: K, values: T) => boolean;
  message: string | ((input: K, values: T) => string);
};

export type ValidationResult<T> = {
  error: boolean;
  message: Message<T>;
};

export type Field<K> = {
  value: Exclude<K, null>;
  error: boolean;
  onChange: <T>(event: React.ChangeEvent<T> | K | undefined) => void;
  onBlur: <T>(event: React.FocusEvent<T> | K | undefined) => void;
  message: string;
  checked: boolean;
};

export type Fields<T> = {
  [K in keyof T]: Field<T[K]>;
};

export const UPDATE_FIELD = "UPDATE_FIELD";
export const RESET_FIELDS = "RESET_FIELDS";
export const VALIDATE_FIELDS = "VALIDATE_FIELDS";
export const ADD_UNSAVEDCHANGES = "ADD_UNSAVEDCHANGES";
export const REMOVE_UNSAVEDCHANGES = "REMOVE_UNSAVEDCHANGES";
export const RESET_UNSAVEDCHANGES = "RESET_UNSAVEDCHANGES";
export const ALL_VALIDATED = "ALL_VALIDATED";

export type ReducerType<T> =
  | {
      type: typeof UPDATE_FIELD;
      payload: {
        field: keyof T;
        value: {
          value?: T[keyof T];
          originalValue?: T[keyof T];
          error?: boolean;
          message?: string;
        };
      };
    }
  | {
      type: typeof RESET_FIELDS;
      payload: {
        fields: Array<keyof T>;
        resetToEmptyValue: boolean;
      };
    }
  | {
      type: typeof ADD_UNSAVEDCHANGES;
      payload: keyof T;
    }
  | {
      type: typeof REMOVE_UNSAVEDCHANGES;
      payload: keyof T;
    }
  | {
      type: typeof RESET_UNSAVEDCHANGES;
    }
  | {
      type: typeof ALL_VALIDATED;
    }
  | {
      type: typeof VALIDATE_FIELDS;
      payload: Array<keyof T>;
    };

export type FormValidation<T> = {
  isValid: boolean;
  wasValidOnMount: boolean;
  unsavedChanges: boolean;
  fields: Fields<T>;
  hasFields: boolean;
  validateFields: (fields?: Array<keyof T>) => boolean;
  saveChanges: () => void;
  setFieldErrors: (
    fields: Partial<Record<keyof T, boolean>> | ValidationProblemDetails,
  ) => void;
  resetFields: (
    fields?: Array<keyof T | `${string}*`>,
    resetToEmptyValue?: boolean,
  ) => void;
  reset: () => void;
  dispatch: React.Dispatch<ReducerType<T>>;
  values: Values<T>;
};

export type Options = {
  validateOnBlur?: boolean;
  validateOnChange?: boolean;
  onChange?: () => void;
  onBlur?: () => void;
  showErrorsOnNonDirtyFields?: boolean;
  emptyStringAsNull?: boolean;
};
